import React from 'react';
import { Section } from '../layout/Section';
import contentData from '../../data/content.json';

/**
 * WineSection Component
 * 
 * Displays wine information including the Mosel-Riesling description,
 * a link to the Schluerfgold website, and information about purchasing wines
 * at the restaurant.
 * 
 * Requirements:
 * - 4.1: Display text about Mosel-Riesling wines and winemaking tradition
 * - 4.2: Display link to https://schluerfgold.de/ (opens in new tab)
 * - 4.3: Indicate wines can be purchased in restaurant
 * - 4.4: Elegant design with wine/vineyard atmosphere
 */
export const WineSection: React.FC = () => {
  const { wine } = contentData;

  return (
    <Section
      id="weine"
      variant="dark"
      aria-labelledby="wine-heading"
    >
      <div className="max-w-4xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-10">
          <p className="text-sm tracking-[0.3em] text-[#c9a84c]/70 uppercase mb-3">Weinpartner</p>
          <h2
            id="wine-heading"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
          >
            Schlürfgold – Weine von der Mosel
          </h2>
          <div className="w-24 h-1 bg-[#c9a84c] mx-auto rounded-full" aria-hidden="true" />
          <p className="mt-4 text-gray-400 text-base max-w-xl mx-auto">
            Wir sind stolz, die Weine von Marcus Stein und Schlürfgold in unserem Restaurant anzubieten.
          </p>
        </div>

        {/* Wine Content Card */}
        <div className="bg-black/70 border border-[#c9a84c]/30 rounded-xl overflow-hidden shadow-2xl">
          {/* Decorative Top Bar */}
          <div className="h-1 bg-gradient-to-r from-transparent via-[#c9a84c] to-transparent" aria-hidden="true" />

          <div className="p-8 sm:p-10 lg:p-12">
            {/* Wine Icon / Decorative Element */}
            <div className="flex justify-center mb-8" aria-hidden="true">
              <svg
                className="w-12 h-12 text-[#c9a84c] opacity-80"
                fill="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                {/* Wine glass icon */}
                <path d="M20 3H4l4 9.5c.5 1.2 1.5 2.1 2.7 2.4L10 21H8v2h8v-2h-2l-.7-6.1c1.2-.3 2.2-1.2 2.7-2.4L20 3zm-8 10c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z" />
              </svg>
            </div>

            {/* Wine Description */}
            <div className="mb-8">
              <p className="text-gray-200 text-base sm:text-lg leading-relaxed text-center sm:text-left">
                {wine.description}
              </p>
            </div>

            {/* Divider */}
            <div className="border-t border-[#c9a84c]/20 my-8" aria-hidden="true" />

            {/* Purchase Info & Link */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
              {/* Purchase in Restaurant Info */}
              <div className="flex items-start gap-3 text-gray-300">
                <svg
                  className="w-5 h-5 text-[#c9a84c] mt-0.5 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <p className="text-sm sm:text-base">
                  Die Weine von Schlürfgold sind auch direkt bei uns im Restaurant erhältlich
                </p>
              </div>

              {/* External Link to Schluerfgold */}
              <a
                href={wine.externalLink}
                target="_blank"
                rel="noopener noreferrer"
                className="
                  inline-flex items-center gap-2 px-6 py-3
                  bg-[#c9a84c] text-[#1a1a1a] font-semibold
                  rounded-lg shadow-lg
                  hover:bg-[#d4b55e] hover:shadow-xl
                  transition-all duration-200
                  focus:outline-none focus:ring-2 focus:ring-[#c9a84c] focus:ring-offset-2 focus:ring-offset-[#111111]
                  whitespace-nowrap
                "
                aria-label="Schluerfgold Weine besuchen (öffnet in neuem Tab)"
              >
                <span>Schluerfgold entdecken</span>
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              </a>
            </div>
          </div>

          {/* Decorative Bottom Bar */}
          <div className="h-1 bg-gradient-to-r from-transparent via-[#c9a84c] to-transparent" aria-hidden="true" />
        </div>

        {/* Drinks Card */}
        <div className="mt-8 bg-black/70 border border-[#c9a84c]/30 rounded-xl overflow-hidden shadow-2xl">
          {/* Decorative Top Bar */}
          <div className="h-1 bg-gradient-to-r from-transparent via-[#c9a84c] to-transparent" aria-hidden="true" />

          <div className="p-8 sm:p-10 lg:p-12">
            {/* Drinks Icon */}
            <div className="flex justify-center mb-8" aria-hidden="true">
              <svg
                className="w-12 h-12 text-[#c9a84c] opacity-80"
                fill="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M21 5V3H3v2l8 9v5H6v2h12v-2h-5v-5l8-9zM7.43 7L5.66 5h12.69l-1.78 2H7.43z"/>
              </svg>
            </div>

            {/* Drinks Description */}
            <div className="mb-8">
              <h3 className="text-xl sm:text-2xl font-semibold text-[#c9a84c] mb-4 text-center sm:text-left">
                Mehr als nur Wein
              </h3>
              <p className="text-gray-200 text-base sm:text-lg leading-relaxed text-center sm:text-left">
                Natürlich darf bei uns auch das kühle Blonde nicht fehlen. Wir bieten eine erfrischende Auswahl an gekühlten Bieren – frisch vom Fass oder aus der Flasche, je nach Stimmung. Wer es lieber prickelnd und bunt mag, findet bei uns außerdem eine vielfältige Auswahl an Aperitifs, Longdrinks und Cocktails. Ob zum Anstoßen vor dem Essen, als Begleitung zum Abend oder einfach so – bei uns ist für jeden Geschmack etwas dabei.
              </p>
            </div>

            {/* Divider */}
            <div className="border-t border-[#c9a84c]/20 my-8" aria-hidden="true" />

            {/* Drinks highlights */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
              {[
                { icon: '🍺', label: 'Bier vom Fass', sub: 'frisch gezapft' },
                { icon: '🍾', label: 'Flaschenbier', sub: 'gekühlte Auswahl' },
                { icon: '🍹', label: 'Aperitifs & Cocktails', sub: 'Longdrinks & mehr' },
              ].map((item) => (
                <div key={item.label} className="flex flex-col items-center gap-2 py-3">
                  <span className="text-2xl" aria-hidden="true">{item.icon}</span>
                  <span className="text-white font-medium text-sm">{item.label}</span>
                  <span className="text-gray-500 text-xs">{item.sub}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Decorative Bottom Bar */}
          <div className="h-1 bg-gradient-to-r from-transparent via-[#c9a84c] to-transparent" aria-hidden="true" />
        </div>
      </div>
    </Section>
  );
};
